// src/screens/seller/support/Support.js
import React, { useState, useEffect, useContext } from "react";
import {
  FiHeadphones,
  FiClock,
  FiCheckCircle,
  FiEye,
  FiTrash2,
  FiX,
  FiArrowRight,
  FiSearch,
  FiPlusCircle,
} from "react-icons/fi";
import { Spinner } from "react-bootstrap";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { useTheme } from "../../../contexts/ThemeContext";
import SellerMasterLayout from "../../../layouts/seller/SellerMasterLayout";
import "../../../assets/css/seller/screens/SellerSupport.css";
import { useNavigate } from "react-router-dom";
import AppContext from "../../../contexts/AppContext";

const STATUS_OPTIONS = ["All Status", "In Progress", "Reopen", "Resolved"];

const SupportManagement = () => {
  const { isDark } = useTheme();
  const { baseUrl, setSelectedSellerTicket, validateForm ,sellerAuthToken } =
    useContext(AppContext);
  const navigate = useNavigate();

  const [showTicketModal, setShowTicketModal] = useState(false);
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [shopGuardModal, setShopGuardModal] = useState(false);
  const [shopGuardType, setShopGuardType] = useState("");
  const [shopStatusLoaded, setShopStatusLoaded] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");

  // NEW: holds error message for each ticket field individually
  const [formErrors, setFormErrors] = useState({});

  const getFilteredTickets = () => {
    let filtered = tickets;
    if (statusFilter !== "All Status") {
      filtered = filtered.filter((t) => t.status === statusFilter);
    }
    if (search.trim()) {
      filtered = filtered.filter(
        (t) =>
          t.id?.toString().includes(search) ||
          t.category?.toLowerCase().includes(search.toLowerCase()) ||
          t.description?.toLowerCase().includes(search.toLowerCase()),
      );
    }
    return filtered;
  };

  const checkShop = () => {
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const formdata = new FormData();
    formdata.append("seller_id", seller?.id);
    axios
      .post(`${baseUrl}/seller/store/getmystore`, formdata ,
        {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },
      )
      .then((res) => {
        const store = res.data.store;
        if (!store?.store_name) {
          setShopStatusLoaded("no-shop");
          setShopGuardType("no-shop");
        } else {
          setShopStatusLoaded(store.status);
          if (store.status === "Pending") setShopGuardType("pending");
        }
      })
      .catch(() => {});
  };

  const viewAllTickets = () => {
    setLoading(true);
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const formdata = new FormData();
    formdata.append("seller_id", seller?.id);
    axios
      .post(`${baseUrl}/seller/support/viewAllTickets`, formdata, 
        {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },
      )
      .then((response) => {
        setTickets(response.data.tickets || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const validateTicketForm = () => {
    let newErrors = {};

    newErrors.category = validateForm([["Category", category]]);
    newErrors.description = validateForm([["Description", description]]);

    console.log("DEBUG validateTicketForm ->", newErrors);

    setFormErrors(newErrors);

    if (newErrors.category || newErrors.description) {
      return false;
    }
    return true;
  };

  const createTicket = () => {
    if (!validateTicketForm()) {
      return;
    }

    setIsCreateLoading(true);
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const formdata = new FormData();
    formdata.append("seller_id", seller?.id);
    formdata.append("category", category);
    formdata.append("description", description);
    axios
      .post(`${baseUrl}/seller/support/createTicket`, formdata,{
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },)
      .then(() => {
        viewAllTickets();
        setCategory("");
        setDescription("");
        setFormErrors({});
        setIsCreateLoading(false);
        setShowTicketModal(false);
        toast.success("Ticket submitted successfully!");
      })
      .catch(() => {
        setIsCreateLoading(false);
        toast.error("Failed to submit ticket. Try again.");
      });
  };

  const deleteTicket = async (id) => {
    const result = await Swal.fire({
      title: "Delete Ticket?",
      text: "This ticket and all its messages will be permanently removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete!",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const formdata = new FormData();
    formdata.append("id", id);
    formdata.append("seller_id", seller?.id);
    axios
      .post(`${baseUrl}/seller/support/deleteTicket`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },)
      .then(() => {
        viewAllTickets();
        toast.success("Ticket deleted.");
      })
      .catch(() => toast.error("Could not delete ticket. Try again."));
  };

  const getStats = () => {
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const fd = new FormData();
    fd.append("seller_id", seller?.id);
    axios
      .post(`${baseUrl}/seller/support/stats`, fd ,
        {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },
      )
      .then((res) => setStatsData(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    viewAllTickets();
    checkShop();
    getStats();
  }, []);

  const filteredTickets = getFilteredTickets();

  return (
    <SellerMasterLayout>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Shop Guard Modal ── */}
      {shopGuardModal && (
        <div
          className="sup-modal-overlay"
          onClick={() =>
            shopStatusLoaded === "Approved" ? setShopGuardModal(false) : null
          }
        >
          <div className="sup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sup-guard-body">
              <button
                className="sup-modal-close sup-guard-close"
                onClick={() => navigate("/seller/dashboard")}
              >
                <FiX size={15} />
              </button>
              <div className="sup-guard-icon">
                {shopGuardType === "pending" ? "⏳" : "🏪"}
              </div>
              <h2 className="sup-guard-title">
                {shopGuardType === "pending"
                  ? "Shop Awaiting Approval"
                  : "Set Up Your Shop First"}
              </h2>
              <p className="sup-guard-desc">
                {shopGuardType === "pending"
                  ? "Your shop is currently under review. You'll be able to raise support tickets once your shop is approved."
                  : "You haven't created your shop yet. Please set up your shop profile before using support features."}
              </p>
              {shopGuardType === "no-shop" && (
                <button
                  className="sup-btn-add sup-guard-btn"
                  onClick={() => navigate("/seller/editstore")}
                >
                  Create My Shop <FiArrowRight size={14} />
                </button>
              )}
              <button
                className="sup-btn-cancel sup-guard-btn"
                onClick={() => navigate("/seller/dashboard")}
              >
                {shopGuardType === "pending"
                  ? "Go to Dashboard"
                  : "Maybe later"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`sup-shell-wrap ${isDark ? "dark-mode" : ""}`}>
        <main className="sup-main">
          <section className="sup-card-shell">
            {/* ── Stat Cards ── */}
            <div className="sup-overview">
              <div className="sup-overview-card">
                <div>
                  <span className="sup-overview-label">Total Tickets</span>
                  <h3 className="sup-overview-value">
                    {statsData?.total_tickets ?? "0"}
                  </h3>
                  <p className="sup-overview-sub">All support tickets</p>
                </div>
                <div className="sup-overview-icon">
                  <FiHeadphones />
                </div>
              </div>

              <div className="sup-overview-card">
                <div>
                  <span className="sup-overview-label">Open Tickets</span>
                  <h3 className="sup-overview-value">
                    {statsData?.open_tickets ?? "0"}
                  </h3>
                  <p className="sup-overview-sub">In progress</p>
                </div>
                <div className="sup-overview-icon sup-overview-icon-alert">
                  <FiClock />
                </div>
              </div>

              <div className="sup-overview-card">
                <div>
                  <span className="sup-overview-label">Resolved</span>
                  <h3 className="sup-overview-value">
                    {statsData?.resolved_tickets ?? "0"}
                  </h3>
                  <p className="sup-overview-sub">Closed tickets</p>
                </div>
                <div className="sup-overview-icon">
                  <FiCheckCircle />
                </div>
              </div>
            </div>

            {/* ── Header / Filters ── */}
            <div className="sup-header">
              <div className="sup-header-left" />
              <div className="sup-header-filters">
                <div className="sup-search">
                  <FiSearch size={15} />
                  <input
                    placeholder="Search tickets"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      className="sup-clear-input-btn"
                      onClick={() => setSearch("")}
                    >
                      <FiX size={12} />
                    </button>
                  )}
                </div>

                <select
                  className="sup-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>

                <button
                  className="sup-topup-btn"
                  onClick={() => {
                    if (
                      shopStatusLoaded === "no-shop" ||
                      shopStatusLoaded === "Pending"
                    ) {
                      setShopGuardModal(true);
                      return;
                    }
                    setShowTicketModal(true);
                  }}
                >
                  <FiPlusCircle size={14} /> Generate Ticket
                </button>
              </div>
            </div>

            {/* ── Tickets Table ── */}
            {/* ── Tickets Table ── */}
            <div className="sup-table-wrapper">
              <table className="sup-table">
                <thead>
                  <tr>
                    <th>Ticket ID</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        <Spinner
                          animation="border"
                          size="sm"
                          className="sup-spinner"
                          style={{ color: "#ff8559" }}
                        />
                        <span className="ms-2 sup-muted sup-loading-text">
                          Loading tickets...
                        </span>
                      </td>
                    </tr>
                  )}

                  {!loading && filteredTickets.length === 0 && (
                    <tr>
                      <td colSpan="5" className="sup-no-results-row">
                        <div style={{ fontSize: "2rem" }}>📦</div>
                        <p className="sup-muted" style={{ marginTop: "8px" }}>
                          No tickets found
                        </p>
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    filteredTickets.length > 0 &&
                    filteredTickets.map((ticket) => (
                      <tr key={ticket.id}>
                        <td className="sup-ticket-id-cell">#{ticket.id}</td>
                        <td>{ticket.category}</td>
                        <td className="sup-desc-cell">{ticket.description}</td>
                        <td>
                          <span
                            className={`sup-status ${
                              ticket.status === "In Progress"
                                ? "sup-status-progress"
                                : ticket.status === "Reopen"
                                  ? "sup-status-reopen"
                                  : "sup-status-resolved"
                            }`}
                          >
                            {ticket.status}
                          </span>
                        </td>
                        <td>
                          <div className="sup-action-btns">
                            <button
                              className="sup-view-btn"
                              onClick={() => {
                                setSelectedSellerTicket(ticket);
                                navigate("/seller/support/support-detail");
                              }}
                            >
                              <FiEye size={12} /> View
                            </button>
                            <button
                              className="sup-delete-btn"
                              onClick={() => deleteTicket(ticket.id)}
                            >
                              <FiTrash2 size={12} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>

      {/* ── Generate Ticket Modal ── */}
      {showTicketModal && (
        <div
          className="sup-modal-overlay"
          onClick={() => setShowTicketModal(false)}
        >
          <div className="sup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sup-modal-header">
              <h3>Generate Support Ticket</h3>
              <button
                className="sup-modal-close"
                onClick={() => setShowTicketModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className="sup-modal-body">
              <div className="sup-form-group">
                <label className="sup-form-label">
                  Category{" "}
                  {formErrors.category && (
                    <span style={{ color: "#ff6b3d" }}>*</span>
                  )}
                </label>
                <select
                  className="sup-input"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">Select category</option>
                  <option value="Payment">Payment Issues</option>
                  <option value="Orders">Order Management</option>
                  <option value="Account">Account & Profile</option>
                  <option value="Technical">Technical Support</option>
                  <option value="Other">Other</option>
                </select>
                {formErrors.category && (
                  <span
                    style={{
                      color: "#ff6b3d",
                      fontSize: "0.8rem",
                      display: "block",
                      marginTop: "4px",
                    }}
                  >
                    {formErrors.category}
                  </span>
                )}
              </div>

              <div className="sup-form-group">
                <label className="sup-form-label">
                  Description{" "}
                  {formErrors.description && (
                    <span style={{ color: "#ff6b3d" }}>*</span>
                  )}
                </label>
                <textarea
                  className="sup-input"
                  rows={5}
                  placeholder="Provide detailed information about your issue..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                {formErrors.description && (
                  <span
                    style={{
                      color: "#ff6b3d",
                      fontSize: "0.8rem",
                      display: "block",
                      marginTop: "4px",
                    }}
                  >
                    {formErrors.description}
                  </span>
                )}
              </div>
            </div>

            <div className="sup-modal-footer">
              <button
                className="sup-btn-cancel"
                onClick={() => setShowTicketModal(false)}
                disabled={isCreateLoading}
              >
                Cancel
              </button>
              <button
                className="sup-btn-submit"
                onClick={createTicket}
                disabled={isCreateLoading}
              >
                {isCreateLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Submitting...
                  </>
                ) : (
                  "Submit Ticket"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </SellerMasterLayout>
  );
};

export default SupportManagement;
